import {makeObservable, observable, reaction, runInAction} from 'mobx';

import {delay} from '@yourcoach/async-utils';

import type {
  CoreUserEventsListParams,
  CoreUserEventsMarkReadAllParams,
  CoreUserEventsMarkReadParams,
  CoreUserEventsReadParams,
} from '../types/core/methods';
import type {EventApiModel} from './types';

import {currentUserStore} from '../user';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';

export class EventStore<
  T extends EventApiModel = EventApiModel,
> extends EntityStore<T, CoreUserEventsReadParams> {
  types: Event['type'][];
  unreadEvents: T[] = [];

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      unreadEvents: observable.shallow,
    });

    reaction(
      () => currentUserStore.user && currentUserStore.user._id,
      () => {
        if (!currentUserStore.user) {
          runInAction(() => {
            this.unreadEvents = [];
          });
        }
      },
    );

    this.types = args[2] ? args[2].types : [];
  }

  async fetchUnread(params: CoreUserEventsListParams = {}) {
    try {
      const query = [
        ['read', '==', null],
        this.types.length ? ['type', 'in', this.types] : null,
      ].filter(Boolean) as Required<CoreUserEventsListParams>['query'];

      if (Array.isArray(params.query) && Array.isArray(query)) {
        query.push(...params.query);
      }

      if (!params.expand) {
        params.expand = {
          event: ['context_ids'],
        };
      }

      const result = await apiRequest<CoreUserEventsListParams>({
        method: 'user.events.list',
        params: {
          cursor: [-1, 0, null],
          ...params,
          query,
        },
      });

      runInAction(() => {
        this.unreadEvents = result._items.map((event: T) => {
          return expandObj<T>(event, {
            expand: params.expand!,
            expanded: result._expanded,
          });
        });
      });
    } catch (error) {
      await delay(2000);
      await this.fetchUnread(params);
    }
  }

  async markRead(event: T, params?: Omit<CoreUserEventsMarkReadParams, '_id'>) {
    const result = await apiRequest<CoreUserEventsMarkReadParams>({
      method: 'user.events.mark_read',
      params: {
        ...params,
        _id: event._id,
      },
    });

    let _event: T;

    if (params && params.expand) {
      _event = expandObj<T>(result.event, {
        expand: params.expand,
        expanded: result._expanded,
      });
    } else {
      _event = result.event;
    }

    const eventIndex = this.unreadEvents.findIndex(
      item => item._id === _event._id,
    );

    if (eventIndex >= 0) {
      runInAction(() => {
        this.unreadEvents.splice(eventIndex, 1);
      });
    }

    return _event;
  }

  async markReadAll(params: CoreUserEventsMarkReadAllParams = {}) {
    const query = [
      this.types.length ? ['type', 'in', this.types] : null,
    ].filter(Boolean) as Required<CoreUserEventsMarkReadAllParams>['query'];

    if (Array.isArray(params.query) && Array.isArray(query)) {
      query.push(...params.query);
    }

    const result = await apiRequest<CoreUserEventsMarkReadAllParams>({
      method: 'user.events.mark_read_all',
      params: {
        cursor: [-1, 0, null],
        ...params,
        query,
      },
    });

    result._items.forEach((event: T) => {
      const eventIndex = this.unreadEvents.findIndex(
        item => item._id === event._id,
      );

      if (eventIndex >= 0) {
        runInAction(() => {
          this.unreadEvents.splice(eventIndex, 1);
        });
      }
    });

    if (result._next) {
      await this.markReadAll({
        ...params,
        cursor: result._next,
      });
    }

    return result;
  }
}

export const createEventStore = <T extends EventApiModel = EventApiModel>(
  types: EventApiModel['type'][] = [],
) =>
  new EventStore<T>(
    'event',
    {
      fetch: 'user.events.read',
    },
    {types},
  );

export const eventStore = new EventStore('event', {
  fetch: 'user.events.read',
});
